import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import axios from 'axios';
import {
  closestCenter,
  DndContext,
  TouchSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  arraySwap,
  SortableContext,
  rectSwappingStrategy
} from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-final-form';
import Icon from '../../../Icon';
import PhotoIcon from '../../../../public/static/images/new_ad/icons/icon-photo.svg';
import ErrorField from '../components/ErrorField';
import Loader from '../../../Loader';
import GalleryItem from '../components/GalleryItem';
import CONFIG from '../../../../config';
import { FORM_VALUES } from '../../../../utils/helpers/constants';

const errorHandler = error => {
  console.error(
    'API Error:',
    error.response ? error.response.data : error.message
  );
};

const Photos = () => {
  const fileInputRef = useRef();
  const { change } = useForm();
  const { t, i18n } = useTranslation();
  const { values } = useFormState();
  const [isError, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const images = values[FORM_VALUES.images] || [];
  const hasImages = images?.length > 0;

  const styles = classNames('item-modal__photo-upload', {
    'item-modal__photo-upload--has-images': hasImages || loading
  });

  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: { delay: 100, tolerance: 5 }
    })
  );

  const onDragEnd = event => {
    const { active, over } = event;

    if (over?.id && active.id !== over.id) {
      const oldIndex = images.findIndex(el => el.id === active.id);
      const newIndex = images.findIndex(el => el.id === over.id);
      const newArray = arraySwap(images, oldIndex, newIndex);
      change(FORM_VALUES.images, newArray);
    }
  };

  const handleAddPhoto = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = formData => {
    axios
      .post(`${CONFIG.photosBaseUrl}/pond`, formData, {
        params: { lang: i18n.language },
        headers: {
          Accept: 'application/json',
          'Content-Type': 'multipart/form-data'
        }
      })
      .then(response => {
        change(FORM_VALUES.images, [
          ...(images || []),
          ...response.data.photos
        ]);
      })
      .catch(errorHandler)
      .finally(() => setLoading(false));
  };

  const handleFileInputChange = event => {
    if (event.target.files && event.target.files.length > 0) {
      const formData = new FormData();
      for (let i = 0; i < event.target.files.length; i += 1) {
        formData.append('images[]', event.target.files[i]);
      }
      setLoading(true);
      handleUpload(formData);
    }
  };

  const handleDelete = id => {
    const updatedList = images?.filter(image => image.id !== id);
    change(FORM_VALUES.images, updatedList);
  };

  const handleRotate = params => {
    axios
      .post(`${CONFIG.photosBaseUrl}/pond/rotate`, null, {
        params: {
          id: params.id,
          angle: params.angle,
          lang: i18n.language
        },
        headers: {
          Accept: 'application/json'
        }
      })
      .then(response => {
        const newValues = images.map(image =>
          image.id === params.id ? response.data : image
        );
        change(FORM_VALUES.images, newValues);
      })
      .catch(errorHandler);
  };

  return (
    <div className="filters__section" style={{ paddingBottom: '5px' }}>
      <div
        className="item-modal__block-header"
        style={{ marginBottom: '10px' }}
      >
        {t('new_ad.photos')}
      </div>
      <div className="item-modal__photo-wrapper">
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          collisionDetection={closestCenter}
        >
          <SortableContext items={images} strategy={rectSwappingStrategy}>
            {images.map(({ id, thumbnail_url: url }) => (
              <GalleryItem
                id={id}
                key={id}
                url={url}
                handleDelete={() => handleDelete(id)}
                handleRotateLeft={() => handleRotate({ id, angle: -90 })}
                handleRotateRight={() => handleRotate({ id, angle: 90 })}
              />
            ))}
          </SortableContext>
          <Loader loading={loading} optionalClass="item-modal__photo-loader" />
        </DndContext>
      </div>
      <div
        tabIndex={0}
        role="button"
        className={styles}
        onClick={handleAddPhoto}
        onKeyPress={handleAddPhoto}
      >
        <Icon width={19} height={15} IconComponent={PhotoIcon} />
        <div className="item-modal__photo-text">{t('new_ad.add_photo')}</div>
        <input
          multiple
          type="file"
          accept="image/*"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
        />
      </div>
      <div className="item-modal__validation__counter">
        {t('new_ad.min_max_photos')}
      </div>
      <ErrorField
        divider={false}
        setError={setError}
        field={FORM_VALUES.images}
        cypress="images-field-error"
        margin={isError ? '10px' : 0}
      />
    </div>
  );
};

export default Photos;
